import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CodexRail } from "@/components/site/CodexRail";
import { useEffect, useState } from "react";
import { ArrowLeft, Share2, Download, Clock, Wind } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { BreathTimer } from "@/components/site/BreathTimer";
import { MeditationTimer } from "@/components/site/MeditationTimer";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import { ActivationCompleteButton } from "@/components/site/ActivationCompleteButton";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { RelatedRail } from "@/components/site/RelatedRail";
import { ReadingChrome } from "@/components/site/ReadingChrome";
import { wisdomForActivation } from "@/lib/cross-links";
import { WISDOM } from "@/lib/content";
import { getReleaseBySlug } from "@/lib/music.functions";
import { getBookBySlug } from "@/lib/books.functions";
import { absoluteUrl } from "@/lib/site";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  ldScript,
  orgRef,
  personRef,
  seoMeta,
  websiteRef,
} from "@/lib/schema";

import { trackViewed } from "@/hooks/use-recently-viewed";
import { useRememberPath } from "@/hooks/use-remember-path";
import { NextExperiences } from "@/components/site/NextExperiences";
import { ReturnPrompt } from "@/components/site/ReturnPrompt";

import { PushOptInButton } from "@/components/site/PushOptInButton";
import { SocialPackButton } from "@/components/site/SocialPackButton";
import { AddToPlaylistButton } from "@/components/site/AddToPlaylistButton";
import { BreathMode } from "@/components/site/BreathMode";
import { ActivationAudioPlayer, ActivationVideoPlayer } from "@/components/site/ActivationMediaPlayers";
import {
  SERIES_BY_SLUG,
  formatPublishDate,
  formatPublishTime,
  type ActivationSeries,
} from "@/lib/activation-series";

type A = Database["public"]["Tables"]["activations"]["Row"];

// Public column allowlist — internal moderation metadata (moderated_by,
// moderated_at, moderation_notes, drafted_by_model, source) stays server-side.
const ACTIVATION_PUBLIC_COLUMNS =
  "id, slug, title, category, time_label, duration, breath_pattern, intention, instruction, phrase, journal, sort_order, created_at, updated_at, activation_number, series, slot, publish_at, status, subtitle, intro, transmission, scroll_card, caption, reflection_question, hero_image_url, wallpaper_url, audio_url, video_url, share_count, view_count, notified_at, theme, difficulty, estimated_minutes, tags, related_gate_n, related_music_slug, related_book_slug, related_wisdom_slug, related_challenge_slug, related_codex_slug, journal_prompt, meditation, narration_url, moderation_status";

export const Route = createFileRoute("/activations/$slug")({
  loader: async ({ params }) => {
    const { data, error } = await supabase
      .from("activations")
      .select(ACTIVATION_PUBLIC_COLUMNS)
      .eq("slug", params.slug)
      .maybeSingle();
    if (error || !data) throw notFound();
    const a = data as A & {
      related_music_slug?: string | null;
      related_book_slug?: string | null;
      related_wisdom_slug?: string | null;
    };
    // Relationships are explicitly saved by an editor and validated on write.
    // Resolve real titles so links and structured data carry descriptive,
    // first-party anchor text — and drop any edge that no longer resolves.
    const [release, book] = await Promise.all([
      a.related_music_slug ? getReleaseBySlug({ data: { slug: a.related_music_slug } }).catch(() => null) : null,
      a.related_book_slug ? getBookBySlug({ data: { slug: a.related_book_slug } }).catch(() => null) : null,
    ]);
    const wisdom = a.related_wisdom_slug
      ? (WISDOM.find((w) => w.slug === a.related_wisdom_slug) ?? null)
      : null;
    const related: { label: string; to: string; kind: string }[] = [];
    if (release) related.push({ label: release.title, to: `/music/${release.slug}`, kind: `${release.type} by Buddha KA` });
    if (book) related.push({ label: book.title, to: `/books/${book.slug}`, kind: "Book by Buddha KA" });
    if (wisdom) related.push({ label: wisdom.title, to: `/wisdom/${wisdom.slug}`, kind: "Wisdom writing" });
    return { activation: data as A, related };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Not found · KAfrequency" }, { name: "robots", content: "noindex" }] };
    const a = loaderData.activation;
    const related = loaderData.related ?? [];
    const desc = a.intention || a.intro || a.subtitle || "A KAfrequency daily activation.";
    const path = `/activations/${a.slug}`;
    const url = absoluteUrl(path);
    return {
      ...seoMeta({
        title: `${a.title} · KAfrequency Activation`,
        description: desc,
        path,
        image: a.hero_image_url ?? PORTRAIT_URL,
        imageAlt: a.hero_image_url ? a.title : PORTRAIT_ALT,
        ogType: "article",
      }),
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "@id": `${url}#activation`,
          name: a.title,
          description: desc,
          url,
          mainEntityOfPage: { "@type": "WebPage", "@id": url, url },
          isPartOf: websiteRef(),
          author: personRef(),
          publisher: orgRef(),
          inLanguage: "en",
          ...(a.hero_image_url ? { image: a.hero_image_url } : {}),
          ...(a.duration ? { totalTime: a.duration } : {}),
          ...(a.instruction
            ? { step: [{ "@type": "HowToStep", name: "Practice", text: a.instruction }] }
            : {}),
          about: [personRef(), orgRef()],
          ...(related.length
            ? {
                mentions: related.map((r) => ({
                  "@type": "CreativeWork",
                  name: r.label,
                  url: absoluteUrl(r.to),
                })),
                relatedLink: related.map((r) => absoluteUrl(r.to)),
              }
            : {}),
        }),
        ldScript(breadcrumbs([["Activations", "/activations"], [a.title, path]])),
      ],
    };
  },

  component: ActivationDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Activation not found.</p>
      <Link to="/activations" className="mt-4 inline-block text-gold-luminous">
        ← Return to the activations
      </Link>
    </div>
  ),
});


function ActivationDetail() {
  const { activation: a, related } = Route.useLoaderData();
  const [journalDraft, setJournalDraft] = useState("");
  const [breathOpen, setBreathOpen] = useState(false);

  // Phase 3B — remember this practice so it can be resumed on a later visit.
  useRememberPath({
    kind: "activation",
    ref: a.slug,
    title: a.title,
    href: `/activations/${a.slug}`,
    countsAsStart: true,
  });

  useEffect(() => {
    const key = `journal:${a.slug}`;
    setJournalDraft(localStorage.getItem(key) ?? "");
    trackViewed({ kind: "activation", slug: a.slug, title: a.title, href: `/activations/${a.slug}` });
  }, [a.slug, a.title]);


  useEffect(() => {
    const key = `journal:${a.slug}`;
    const t = setTimeout(() => localStorage.setItem(key, journalDraft), 400);
    return () => clearTimeout(t);
  }, [journalDraft, a.slug]);

  async function share() {
    if (typeof navigator === "undefined") return;
    const url = window.location.href;
    const payload = { title: a.title, text: a.intention ?? "", url };
    try {
      if (navigator.share) await navigator.share(payload);
      else if (navigator.clipboard) await navigator.clipboard.writeText(url);
    } catch { /* user dismissed */ }
    // Best-effort share count. RLS blocks anonymous updates; admins/authors will still bump it.
    try {
      await supabase.from("activations").update({ share_count: (a.share_count ?? 0) + 1 }).eq("id", a.id);
    } catch { /* ignore */ }
  }

  const series = a.series ? SERIES_BY_SLUG[a.series as ActivationSeries] : null;
  const breathPattern = (a.breath_pattern === "box" || a.breath_pattern === "coherent" || a.breath_pattern === "478")
    ? a.breath_pattern
    : "box";

  return (
    <article className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
      <ReadingChrome completionKey={`activation:${a.slug}`} />
      <Link to="/activations" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
        <ArrowLeft size={14} /> All Activations
      </Link>

      {/* Hero */}
      <div className="mt-8 overflow-hidden rounded-2xl border-gold border">
        <ReleaseCover coverUrl={a.hero_image_url} label={a.title} aspect="video" alt={a.title} loading="eager" />
      </div>

      <header className="mt-10 grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            {a.activation_number ? `Activation № ${a.activation_number}` : "Preview"}
            {series && <> · <Link to="/activations/series/$series" params={{ series: series.slug }} className="hover:underline">{series.label}</Link></>}
          </p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl md:text-6xl">{a.title}</h1>
          {a.subtitle && <p className="mt-3 font-serif text-xl text-ivory/90">{a.subtitle}</p>}
          <p className="mt-4 text-sm text-muted-foreground">
            {formatPublishDate(a.publish_at)}{a.publish_at ? ` · ${formatPublishTime(a.publish_at)}` : ""}
          </p>
          {a.intro && <p className="mt-6 max-w-2xl text-base text-muted-foreground italic">{a.intro}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <FavoriteButton kind="activation" slug={a.slug} />
          <AddToPlaylistButton activationId={a.id} />
          <PushOptInButton compact />
          <button onClick={share} aria-label="Share" className="inline-flex h-11 w-11 items-center justify-center rounded-full border hairline hover:border-gold hover:text-gold">
            <Share2 size={16} />
          </button>
          {a.hero_image_url && (
            <a href={a.hero_image_url} download aria-label="Download image" className="inline-flex h-11 w-11 items-center justify-center rounded-full border hairline hover:border-gold hover:text-gold">
              <Download size={16} />
            </a>
          )}
        </div>
      </header>

      <div className="mt-8 flex flex-wrap gap-3">
        <ActivationCompleteButton activationSlug={a.slug} />
        <button onClick={() => setBreathOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold">
          <Wind size={14} /> Enter Breath Mode
        </button>
        <SocialPackButton
          title={a.title}
          subtitle={a.subtitle}
          scrollCard={a.scroll_card}
          activationNumber={a.activation_number}
          seriesLabel={series?.label ?? null}
          heroImageUrl={a.hero_image_url}
          slug={a.slug}
        />
      </div>

      {(() => {
        const ax = a as A & {
          tags?: string[] | null;
          theme?: string | null;
          difficulty?: string | null;
          estimated_minutes?: number | null;
          narration_url?: string | null;
          journal_prompt?: string | null;
          meditation?: string | null;
          related_gate_n?: number | null;
          related_music_slug?: string | null;
          related_book_slug?: string | null;
          related_wisdom_slug?: string | null;
        };
        const chips: string[] = [];
        if (ax.theme) chips.push(ax.theme);
        if (ax.difficulty) chips.push(ax.difficulty);
        if (ax.estimated_minutes) chips.push(`${ax.estimated_minutes} min`);
        for (const t of ax.tags ?? []) chips.push(t);
        return chips.length ? (
          <div className="mt-6 flex flex-wrap gap-2">
            {chips.map((c) => (
              <span key={c} className="rounded-full border hairline px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{c}</span>
            ))}
          </div>
        ) : null;
      })()}

      {(a.audio_url || a.video_url || (a as { narration_url?: string }).narration_url) && (
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {a.video_url && <ActivationVideoPlayer src={a.video_url} poster={a.hero_image_url} />}
          {(a as { narration_url?: string }).narration_url && (
            <ActivationAudioPlayer src={(a as { narration_url?: string }).narration_url!} title={`${a.title} · Narration`} />
          )}
          {a.audio_url && <ActivationAudioPlayer src={a.audio_url} title={a.title} />}
        </div>
      )}

      <div className="mt-12 grid gap-10 lg:grid-cols-[1.1fr_1fr]">
        <div className="space-y-10">
          {a.transmission && (
            <section>
              <h2 className="font-serif text-2xl text-gold-luminous">Transmission</h2>
              <div className="mt-4 whitespace-pre-line text-lg leading-relaxed text-ivory/90">
                {a.transmission}
              </div>
            </section>
          )}

          {a.instruction && (
            <section>
              <h2 className="font-serif text-2xl text-gold-luminous">Practice</h2>
              <p className="mt-3 whitespace-pre-line text-muted-foreground">{a.instruction}</p>
              {a.duration && (
                <p className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock size={12} /> Duration: {a.duration}
                </p>
              )}
            </section>
          )}

          {a.scroll_card && (
            <section className="rounded-xl border-gold border bg-charcoal p-6 sm:p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Scroll Card</p>
              <p className="mt-3 font-serif text-2xl italic text-ivory">"{a.scroll_card}"</p>
            </section>
          )}

          {a.phrase && (
            <section className="rounded-xl border hairline bg-charcoal p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Vibrational Phrase</p>
              <p className="mt-3 font-serif text-xl italic text-ivory">"{a.phrase}"</p>
            </section>
          )}

          {(a.journal || a.reflection_question) && (
            <section>
              <h2 className="font-serif text-2xl text-gold-luminous">Journal</h2>
              {a.journal && <p className="mt-3 font-serif text-lg italic text-ivory/90">{a.journal}</p>}
              {a.reflection_question && (
                <p className="mt-3 text-sm text-muted-foreground">Reflection: {a.reflection_question}</p>
              )}
              <textarea
                value={journalDraft}
                onChange={(e) => setJournalDraft(e.target.value)}
                className="mt-4 w-full min-h-[140px] rounded-md border hairline bg-obsidian p-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                placeholder="Write freely. Saved locally to this device."
              />
            </section>
          )}

          {a.caption && (
            <section>
              <h2 className="font-serif text-2xl text-gold-luminous">Share</h2>
              <p className="mt-3 whitespace-pre-line text-sm text-muted-foreground">{a.caption}</p>
              <button
                onClick={() => { navigator.clipboard?.writeText(a.caption ?? ""); }}
                className="mt-3 rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold"
              >
                Copy caption
              </button>
            </section>
          )}

          {(a.wallpaper_url) && (
            <section>
              <h2 className="font-serif text-2xl text-gold-luminous">Wallpaper</h2>
              <a href={a.wallpaper_url} download className="mt-3 inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold hover:text-gold">
                <Download size={14} /> Download wallpaper
              </a>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <BreathTimer pattern={breathPattern} cycles={6} />
          <MeditationTimer />
          {series && (
            <div className="rounded-xl border hairline bg-charcoal p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">Series {series.number}</p>
              <h3 className="mt-2 font-serif text-2xl">{series.label}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{series.tagline}</p>
              <Link to="/activations/series/$series" params={{ series: series.slug }} className="mt-4 inline-flex text-xs uppercase tracking-[0.25em] text-gold hover:underline">
                Enter the series →
              </Link>
            </div>
          )}
        </aside>
      </div>

      {(() => {
        const ax = a as A & {
          related_gate_n?: number | null;
          related_codex_slug?: string | null;
        };
        const links: { label: string; to: string; kind?: string }[] = [];
        if (ax.related_gate_n) links.push({ label: `Gate № ${ax.related_gate_n}`, to: `/gates/${ax.related_gate_n}`, kind: "Gate of consciousness" });
        // Editor-saved relationships, resolved to real titles in the loader.
        for (const r of related) links.push({ label: r.label, to: r.to, kind: r.kind });
        if (ax.related_codex_slug) links.push({ label: "Codex concept", to: `/codex/${ax.related_codex_slug}`, kind: "Codex" });
        // Reverse edge: essays that name this activation as their practice.
        for (const w of wisdomForActivation(a.slug)) {
          if (!links.some((l) => l.to === `/wisdom/${w.slug}`)) {
            links.push({ label: w.title, to: `/wisdom/${w.slug}`, kind: "Wisdom writing" });
          }
        }
        return links.length ? (
          <section className="mt-12 rounded-xl border hairline bg-charcoal p-6">
            <h2 className="font-serif text-2xl text-gold-luminous">Related in the ecosystem</h2>
            <ul className="mt-4 space-y-2 text-sm">
              {links.map((l) => (
                <li key={l.to}>
                  <a href={l.to} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">{l.label}</a>
                  {l.kind && <span className="text-muted-foreground"> — {l.kind}</span>}
                </li>
              ))}
            </ul>
          </section>
        ) : null;
      })()}


      <CodexRail entityKind="activation" entityRef={a.slug} heading="Concepts this activation practices" />
      <RelatedRail kind="activation" slug={a.slug} heading="Deepen the practice" />
      <NextExperiences surface="activation_detail" />
      <ReturnPrompt surface="activation_detail" />
      {breathOpen && <BreathMode pattern={breathPattern} onClose={() => setBreathOpen(false)} />}
    </article>
  );
}
